const HIGH_ENTROPY_HINTS = Object.freeze([
  'architecture',
  'bitness',
  'fullVersionList',
  'model',
  'platformVersion',
]);

const UA_DATA_TIMEOUT_MS = 100;
const MAX_BRANDS = 8;
const MAX_VERSION_PARTS = 8;
const MAX_VALUE_LENGTH = 128;

/**
 * Collect one bounded OpenRTB-shaped high-entropy User-Agent value for all
 * placements handled by this tag instance. Unsupported browsers, incomplete
 * values, timeout, and API failure deliberately resolve to undefined so the
 * Exchange performs its normal User-Agent-only lookup.
 */
export async function collectDeviceSua() {
  try {
    const uaData = globalThis.navigator?.userAgentData;
    if (!uaData || typeof uaData.getHighEntropyValues !== 'function')
      return undefined;

    const values = await settleWithin(
      Promise.resolve().then(() => uaData.getHighEntropyValues(HIGH_ENTROPY_HINTS)),
      UA_DATA_TIMEOUT_MS,
    );
    return values ? buildDeviceSua(uaData, values) : undefined;
  } catch {
    return undefined;
  }
}

function buildDeviceSua(uaData, values) {
  const browserValues = Array.isArray(values.fullVersionList) && values.fullVersionList.length > 0
    ? values.fullVersionList
    : uaData.brands;
  const browsers = parseBrands(browserValues);
  const platform = requiredString(values.platform ?? uaData.platform);
  const platformVersion = parseOptionalVersion(values.platformVersion);
  const model = requiredString(values.model);
  const architecture = optionalString(values.architecture);
  const bitness = optionalString(values.bitness);

  if (!browsers || !platform || !platformVersion || !model
    || architecture === null || bitness === null) {
    return undefined;
  }

  return {
    browsers,
    platform: {
      brand: platform,
      version: platformVersion,
    },
    mobile: Boolean(values.mobile ?? uaData.mobile),
    architecture,
    bitness,
    model,
  };
}

function parseBrands(values) {
  if (!Array.isArray(values) || values.length === 0 || values.length > MAX_BRANDS)
    return null;

  const parsed = values.map(value => {
    const brand = requiredString(value?.brand);
    const version = parseVersion(value?.version);
    return brand && version ? { brand, version } : null;
  });

  return parsed.every(Boolean) ? parsed : null;
}

function parseVersion(value) {
  const normalized = requiredString(value);
  if (!normalized)
    return null;

  const parts = normalized.split('.').map(part => part.trim());
  return parts.length <= MAX_VERSION_PARTS
    && parts.every(part => part.length > 0 && part.length <= MAX_VALUE_LENGTH)
    ? parts
    : null;
}

function parseOptionalVersion(value) {
  if (value == null)
    return [];
  if (typeof value !== 'string')
    return null;
  if (value.trim() === '')
    return [];
  return parseVersion(value);
}

function requiredString(value) {
  if (typeof value !== 'string')
    return null;
  const normalized = value.trim();
  return normalized.length > 0 && normalized.length <= MAX_VALUE_LENGTH
    ? normalized
    : null;
}

function optionalString(value) {
  if (value == null)
    return '';
  if (typeof value !== 'string')
    return null;
  const normalized = value.trim();
  return normalized.length <= MAX_VALUE_LENGTH ? normalized : null;
}

async function settleWithin(promise, timeoutMs) {
  let timeoutId;
  const timeout = new Promise(resolve => {
    timeoutId = setTimeout(() => resolve(undefined), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeout]);
  } catch {
    return undefined;
  } finally {
    clearTimeout(timeoutId);
  }
}
